/**
 * Provider-native option registry for LinkedIn Sponsored Content campaigns.
 *
 * Sibling of `../meta/options.ts` and `../google/options.ts`, and the single
 * source of truth for every LinkedIn campaign enum. The wizard, the contract
 * schemas in `../contracts.ts` and the compiler all read from here so that a
 * value LinkedIn rejects is a type error rather than a 422 at the irreversible
 * step.
 *
 * ## Provenance — these lists were PROBED, not read off a doc page
 *
 * On 2026-09-09 every value below was sent to the live Marketing API
 * (`Linkedin-Version: 202608`; the write boundary now emits 202609 and no
 * 202609 probe is recorded here yet) against ad account `550720156` and its
 * accept/reject recorded. The technique is worth reusing: LinkedIn validates
 * the request schema BEFORE it checks authorization, so a POST that omits the
 * required fields returns a 422 listing every violation at once — including
 * `"X" is not an enum symbol` — and creates nothing. Enum membership is
 * therefore free to verify and needs no write permission.
 *
 * Full results: `docs/planning/PHASE0-linkedin-probe-results-2026-09-09.md`.
 *
 * ⚠️ Each list is "every candidate tested", NOT "every symbol LinkedIn
 * accepts". LinkedIn does not enumerate valid symbols in its error, so a value
 * absent below was either tested and rejected (recorded in the doc) or never
 * tested. Treat these as closed sets for what Mora EMITS, never as a complete
 * description of the provider.
 */

/**
 * Campaign objectives.
 *
 * 🔴 Campaign and Campaign **Group** share ONE vocabulary and it is SINGULAR.
 * This contradicts the atomic spec §1 and handoff D14, which claimed the
 * plural forms were required at campaign level and that 4 of 7 objectives in
 * `../contracts.ts` would be rejected. Probed at both levels: the plurals
 * (`WEBSITE_VISITS`, `VIDEO_VIEWS`, `WEBSITE_CONVERSIONS`, `JOB_APPLICANTS`)
 * are rejected at BOTH, and adopting them would have converted four working
 * objectives into hard 422s.
 *
 * Do not split this into campaign/group constants "with a test asserting they
 * differ" — they do not differ, and such a test would pin a false fact.
 *
 * `TALENT_LEADS` is deliberately absent: probed and rejected at both levels.
 */
export const LINKEDIN_OBJECTIVE_TYPES = [
  'BRAND_AWARENESS',
  'WEBSITE_VISIT',
  'ENGAGEMENT',
  'VIDEO_VIEW',
  'LEAD_GENERATION',
  'WEBSITE_CONVERSION',
  'JOB_APPLICANT',
  'CREATIVE_ENGAGEMENT',
] as const;
export type LinkedInObjectiveType = (typeof LINKEDIN_OBJECTIVE_TYPES)[number];

/**
 * How LinkedIn's own Campaign Manager presents these objectives.
 *
 * 🔴 **The labels are PLURAL and the API values are SINGULAR.** "Website
 * visits" is `WEBSITE_VISIT`. This is almost certainly the origin of handoff
 * D14, which read the plural labels off a doc table or the UI and concluded
 * the API wanted plurals — it does not, and sending them breaks four
 * objectives. Never derive an API value from a label.
 *
 * Transcribed from the live Campaign Manager objective picker (2026-09-09):
 * three groups, seven objectives, in this order.
 *
 * `CREATIVE_ENGAGEMENT` is a valid API value but is **not offered in the UI**,
 * so it is deliberately absent here — Mora's picker should match LinkedIn's,
 * not invent an eighth objective a marketer has never seen.
 */
export const LINKEDIN_OBJECTIVE_GROUPS = ['Awareness', 'Consideration', 'Conversions'] as const;
export type LinkedInObjectiveGroup = (typeof LINKEDIN_OBJECTIVE_GROUPS)[number];

export interface LinkedInObjectiveOption {
  value: LinkedInObjectiveType;
  /** LinkedIn's own wording. Do not paraphrase — a marketer is matching this against Campaign Manager. */
  label: string;
  group: LinkedInObjectiveGroup;
  /** What the advertiser is buying, in plain language. */
  description: string;
}

export const LINKEDIN_OBJECTIVE_OPTIONS: readonly LinkedInObjectiveOption[] = [
  {
    value: 'BRAND_AWARENESS',
    label: 'Brand awareness',
    group: 'Awareness',
    description: 'Show your ad to as many relevant people as possible.',
  },
  {
    value: 'WEBSITE_VISIT',
    label: 'Website visits',
    group: 'Consideration',
    description: 'Send people to your site or landing page.',
  },
  {
    value: 'ENGAGEMENT',
    label: 'Engagement',
    group: 'Consideration',
    description: 'Get reactions, comments, shares and Page follows.',
  },
  {
    value: 'VIDEO_VIEW',
    label: 'Video views',
    group: 'Consideration',
    description: 'Get people to watch your video.',
  },
  {
    value: 'LEAD_GENERATION',
    label: 'Lead generation',
    group: 'Conversions',
    description: 'Collect leads in a form on LinkedIn, pre-filled from their profile.',
  },
  {
    value: 'WEBSITE_CONVERSION',
    label: 'Website conversions',
    group: 'Conversions',
    description: 'Drive an action on your site. Requires conversion tracking.',
  },
  {
    value: 'JOB_APPLICANT',
    label: 'Job applicants',
    group: 'Conversions',
    description: 'Get people to see and apply to your jobs.',
  },
];

/**
 * `objectiveType` is OPTIONAL on a Campaign Group — it is absent from the
 * required-field list, and the live group `1199663084` carries
 * `objectiveType: null`. Required on a Campaign.
 */
export const LINKEDIN_CAMPAIGN_TYPES = [
  'SPONSORED_UPDATES',
  'TEXT_AD',
  'SPONSORED_INMAILS',
  'DYNAMIC',
] as const;
export type LinkedInCampaignType = (typeof LINKEDIN_CAMPAIGN_TYPES)[number];

export const LINKEDIN_CAMPAIGN_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'PAUSED',
  'ARCHIVED',
  'COMPLETED',
  'CANCELED',
  'PENDING_DELETION',
] as const;
export type LinkedInCampaignStatus = (typeof LINKEDIN_CAMPAIGN_STATUSES)[number];

/**
 * ⚠️ `LEAD_GENERATION_FORM_SPONSORED_CONTENT` is NOT a member of this enum —
 * probed and rejected. The atomic spec §4 assigns it as the `format` for both
 * of its `LEAD_GENERATION` matrix rows; those rows cannot work as written.
 *
 * The lead form is attached at the **Creative** via `leadgenCallToAction`, not
 * by a distinct campaign format — so lead gen is expected to run on an ordinary
 * Sponsored Content format. That is an INFERENCE from the object model and is
 * NOT verified: confirming it needs a real create, which is blocked on the ad
 * account being added to the app's Account Management list.
 */
export const LINKEDIN_FORMATS = [
  'STANDARD_UPDATE',
  'SPONSORED_UPDATE_NATIVE_DOCUMENT',
  'SINGLE_VIDEO',
  'CAROUSEL',
  'TEXT_AD',
  'SPOTLIGHT',
  'JOBS',
  'SPONSORED_UPDATE_EVENT',
  'SPONSORED_MESSAGE',
] as const;
export type LinkedInFormat = (typeof LINKEDIN_FORMATS)[number];

/** Release 1 emits Sponsored Content only; the rest are typed but not offered. */
export const LINKEDIN_RELEASE_1_FORMATS = [
  'STANDARD_UPDATE',
  'SPONSORED_UPDATE_NATIVE_DOCUMENT',
] as const satisfies readonly LinkedInFormat[];

export const LINKEDIN_OPTIMIZATION_TARGET_TYPES = [
  'NONE',
  'MAX_REACH',
  'MAX_IMPRESSION',
  'MAX_CLICK',
  'MAX_CONVERSION',
  'MAX_VIDEO_VIEW',
  'MAX_LEAD',
  'MAX_QUALIFIED_LEAD',
  'ENHANCED_CONVERSION',
  'TARGET_COST_PER_CLICK',
  'TARGET_COST_PER_IMPRESSION',
  'TARGET_COST_PER_VIDEO_VIEW',
  'CAP_COST_AND_MAXIMIZE_CLICKS',
  'CAP_COST_AND_MAXIMIZE_IMPRESSIONS',
  'CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS',
  'CAP_COST_AND_MAXIMIZE_LEADS',
] as const;
export type LinkedInOptimizationTargetType = (typeof LINKEDIN_OPTIMIZATION_TARGET_TYPES)[number];

export const LINKEDIN_COST_TYPES = ['CPM', 'CPC', 'CPV'] as const;
export type LinkedInCostType = (typeof LINKEDIN_COST_TYPES)[number];

/**
 * `NOT_DECLARED` is the value LinkedIn itself writes on a campaign created in
 * Campaign Manager (observed on live campaign `879091884`) and is the correct
 * default for an advertiser who has not made a political declaration.
 * `politicalIntent` is required with no default, so it must always be sent.
 */
export const LINKEDIN_POLITICAL_INTENTS = ['NOT_DECLARED', 'NOT_POLITICAL', 'POLITICAL'] as const;
export type LinkedInPoliticalIntent = (typeof LINKEDIN_POLITICAL_INTENTS)[number];

export const LINKEDIN_CREATIVE_SELECTIONS = ['OPTIMIZED', 'ROUND_ROBIN'] as const;
export type LinkedInCreativeSelection = (typeof LINKEDIN_CREATIVE_SELECTIONS)[number];

/** `EVEN` is NOT a member — probed and rejected. */
export const LINKEDIN_PACING_STRATEGIES = ['LIFETIME', 'ACCELERATED'] as const;
export type LinkedInPacingStrategy = (typeof LINKEDIN_PACING_STRATEGIES)[number];

/**
 * The fields LinkedIn reports as `required but not found and has no default
 * value` when a Campaign create omits everything. PROVEN by a single 422 that
 * listed all six at once.
 *
 * ⚠️ `campaignGroup` is NOT in this list, contrary to atomic spec §5 which
 * marks it required. It may be defaulted or enforced later in the pipeline;
 * unresolved until a real create is possible.
 */
export const LINKEDIN_CAMPAIGN_REQUIRED_FIELDS = [
  'type',
  'status',
  'runSchedule',
  'locale',
  'offsiteDeliveryEnabled',
  'politicalIntent',
] as const;

/**
 * Money crosses exactly one boundary, here.
 *
 * Mora stores minor units (integer cents) because that is Meta's convention.
 * LinkedIn takes a **major-currency decimal string**: a campaign budgeted at
 * $200/day reads `"200"`, and live campaign `879091884` returns
 * `unitCost.amount: "35.4"`. Getting this wrong is silent and off by 100x.
 *
 * Note LinkedIn does not zero-pad — it returned `"35.4"`, not `"35.40"` — so
 * never compare these strings for equality when reading back; compare
 * numerically. This function emits the padded 2dp form, which LinkedIn accepts.
 */
export function minorUnitsToLinkedInAmount(minorUnits: number): string {
  if (!Number.isInteger(minorUnits)) {
    throw new Error(`LinkedIn amount must come from integer minor units, received ${minorUnits}`);
  }
  if (minorUnits < 0) {
    throw new Error(`LinkedIn amount cannot be negative, received ${minorUnits}`);
  }
  const major = Math.trunc(minorUnits / 100);
  const cents = minorUnits % 100;
  return `${major}.${String(cents).padStart(2, '0')}`;
}

/** Inverse, for reading a provider value back into Mora's storage unit. */
export function linkedInAmountToMinorUnits(amount: string): number {
  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) {
    throw new Error(`LinkedIn amount is not numeric: ${amount}`);
  }
  return Math.round(parsed * 100);
}

/**
 * `costType` is DERIVED, never chosen.
 *
 * Offering it as a free field is the commonest cause of
 * `INVALID_CAMPAIGN_CONFIGURATION`, so the wizard must not present it. It is a
 * function of the optimization target, whose bidding family determines the
 * billing unit.
 *
 * ## Evidence class, after probing the live API on 2026-09-09
 *
 * **Proven:** LinkedIn DOES validate `costType` against `objectiveType`, but
 * only on UPDATE. Patching campaign 893034354 (`WEBSITE_VISIT`) to `CPV` was
 * refused:
 *
 *     /Campaign/costType cannot be set to CPV if /Campaign/objectiveType is set to WEBSITE_VISIT
 *
 * **Proven:** the value this function derives is accepted — the campaign was
 * created and read back with the derived `CPM`.
 *
 * **NOT proven:** that the derived value is the ONLY correct one. Patching the
 * same campaign to `CPC` also returned 204, so `WEBSITE_VISIT` + `MAX_CLICK`
 * admits both. The mapping below follows LinkedIn's documented bidding families
 * ("CPM if MAX, else CPC"); it is a safe choice within the permitted set rather
 * than a uniquely determined one.
 *
 * 🔴 **What this does NOT protect against:** `optimizationTargetType` is not
 * validated against the objective or format at all — patching a
 * `STANDARD_UPDATE` campaign to `MAX_VIDEO_VIEW` returned 204. So a correct
 * `costType` does not imply a servable campaign, and
 * `configuration-matrix.ts` remains the only thing enforcing the pairing.
 */
export function deriveLinkedInCostType(
  optimizationTargetType: LinkedInOptimizationTargetType,
): LinkedInCostType {
  switch (optimizationTargetType) {
    case 'TARGET_COST_PER_VIDEO_VIEW':
      return 'CPV';
    case 'TARGET_COST_PER_CLICK':
    case 'ENHANCED_CONVERSION':
      return 'CPC';
    default:
      // Every auto-bid (MAX_*), every cost cap (CAP_COST_AND_MAXIMIZE_*),
      // TARGET_COST_PER_IMPRESSION, and manual NONE all bill per impression.
      return 'CPM';
  }
}

/**
 * LinkedIn's call-to-action button, which a Sponsored Content creative carries as
 * `contentCallToActionLabel`. The button is chosen from this fixed list and
 * rendered by LinkedIn; free text never appears on it. Values and their on-screen
 * text are verbatim from the Post API schema (Linkedin-Version 2026-08, not re-read at 2026-09):
 * https://learn.microsoft.com/en-us/linkedin/marketing/community-management/shares/post-api-schema?view=li-lms-2026-08
 * `BUY_NOW` and `SHOP_NOW` require API version 202504 or later.
 */
export const LINKEDIN_CTA_LABELS = {
  APPLY: 'Apply',
  DOWNLOAD: 'Download',
  VIEW_QUOTE: 'View Quote',
  LEARN_MORE: 'Learn More',
  SIGN_UP: 'Sign Up',
  SUBSCRIBE: 'Subscribe',
  REGISTER: 'Register',
  JOIN: 'Join',
  ATTEND: 'Attend',
  REQUEST_DEMO: 'Register Demo',
  SEE_MORE: 'See More',
  BUY_NOW: 'Buy Now',
  SHOP_NOW: 'Shop Now',
} as const;

export type LinkedInCtaLabel = keyof typeof LINKEDIN_CTA_LABELS;

/**
 * The text LinkedIn would print on the button for `value`, or null when `value`
 * is not one of LinkedIn's labels. Accepts the enum (`LEARN_MORE`) or its exact
 * on-screen text (`Learn More`), case-insensitively. It deliberately does not
 * guess a label from a sentence ("Book a demo to see…"): that would show a
 * marketer a button they never chose.
 */
export function linkedInCtaDisplayLabel(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const asKey = trimmed.toUpperCase().replace(/[\s-]+/g, '_');
  if (asKey in LINKEDIN_CTA_LABELS) return LINKEDIN_CTA_LABELS[asKey as LinkedInCtaLabel];
  const byText = Object.values(LINKEDIN_CTA_LABELS).find(
    (label) => label.toLowerCase() === trimmed.toLowerCase(),
  );
  return byText ?? null;
}
